<template>
  <div class="w-full">
    <div class="flex flex-row justify-between text-sm uppercase align-middle text-blue-light">
      <div>
        <b-button
          @click="addTaskButtonClicked"
          @reload="() => reload()"
          v-if="canAdd && !isTaskEditing && !isBulkCompleting"
          type="is-small is-primary"
          class="font-semibold uppercase">{{ $t("add_task") }}</b-button>
        <!-- edit -->
        <b-button
          @click="editTasksButtonClicked"
          v-if="! isTaskEditing && !isBulkCompleting && project.can_admin"
          type="is-small is-primary"
          class="font-semibold uppercase">{{ $t("edit_tasks") }}</b-button>
        <!-- completar en lote -->
        <b-button
          @click="isBulkCompleting = true"
          v-if="canBulkCompleteTasks && !isBulkCompleting"
          type="is-small is-primary"
          icon-left="check-double"
          icon-pack="fas"
          class="font-semibold uppercase">{{ $t("complete_tasks") }}</b-button>
        <template v-if="isBulkCompleting">
          <b-button
            @click="onBulkCompleteTasks"
            type="is-small is-success"
            icon-left="check-double"
            icon-pack="fas"
            :disabled="bulkCompleteTaskIds.length === 0"
            :loading="isSavingBulkComplete"
            class="font-semibold uppercase">{{ $t("complete_selected_tasks", { count: bulkCompleteTaskIds.length }) }}</b-button>
          <b-button
            @click="cancelBulkComplete"
            type="is-small is-danger"
            :disabled="isSavingBulkComplete"
            class="font-semibold uppercase">{{ $t("cancel") }}</b-button>
        </template>
        <!-- save -->
        <b-button
          @click="saveTasksButtonClicked"
          v-if="isTaskEditing"
          type="is-small is-success"
          :disabled="calculeWeight() !== 100.00"
          class="font-semibold uppercase">{{ $t("save") }}</b-button>
        <!-- cancel -->
        <b-button
          @click="editTasksButtonClicked"
          v-if="isTaskEditing"
          type="is-small is-danger"
          class="font-semibold uppercase">{{ $t("cancel") }}</b-button>
        <!-- distribute Wheight -->
        <b-button
          @click="distributeTaskWeights"
          v-if="isTaskEditing"
          type="is-small is-primary"
          class="font-semibold uppercase">{{ $t("distribute_weights") }}
        </b-button>
      </div>
    </div>

    <div v-if="isLoadingTask" class="my-4 text-center">
      <scale-loader />
    </div>

    <div v-else class="flex flex-row justify-between mt-3 text-sm uppercase align-middle text-blue-light">
      <div>
        <span v-if="listTasks.length" class="font-semibold">{{ listTasks.length }}</span> {{ !listTasks.length ? $t("no_tasks_found") : $t("tasks_under_execution") }}
        <span v-if="isTaskEditing" class="font-semibold text-red"> Total: {{ calculeWeight() }}</span>
      </div>
    </div>
    <span v-if="calculeWeight() > 100" class="font-semibold text-red">{{ $t('assignment_task_over_ponderation') }}</span>

    <div class="w-full" v-if="!isLoadingTask && listTasks.length">

      <assignment-task-table
        :isTaskEditing="isTaskEditing"
        :listTasks="listTasks"
        :canManage="canManage"
        :canAdd="canAdd"
        :entityId="entityId"
        :subactivityId="subactivityId"
        :activityName="activityName"
        :onEditTask="(task, field) => onEditTask(task, field)"
        :locked="isLockAssignmentTask()"
        :expandedTaskIds="expandedTaskIds"
        :openHistoryTaskIds="openHistoryTaskIds"
        :bulkCompleting="isBulkCompleting"
        :bulkSelectedTaskIds="bulkSelectedTaskIds"
        @bulk-select="onBulkSelect"
        @update:list-tasks="(value) => listTasks = value"
        @reload="() => reload()"
      />

    </div>
    <!-- Modal para crear tarea -->
    <task-modal
      ref="task_modal"
      entity-type="activity"
      :entity-id="entityId"
      @reload="() => reload()"
    />

    <!-- modal completar tareas en lote -->
    <assignment-bulk-complete-modal
      ref="assignment_bulk_complete_modal"
      :loading="isSavingBulkComplete"
      @confirm="submitBulkCompleteTasks"
    />

  </div>
</template>

<script>
import assignmentTaskTable from './assignment-task-table.vue';
import taskModal from "·/components/task-modal";
import assignmentUpdateModal from "·/components/assignment-update-modal";
import taskUpdateModal from "·/components/task-update-modal";
import assignmentCompletedModal from "·/components/assignment-completed-modal";
import assignmentRegisterTaskModal from "·/components/assignment-register-task-modal";
import assignmentDocumentPreview from "../assignment-document-preview";

import assignmentCommentTaskModal from "·/components/assignment-comment-task-modal";
import assignmentLineEditModal from "·/components/assignment-line-edit-modal";
import AssignmentValidateQuantityModal from '·/components/assignment-validate-quantity-modal';
import assignmentBulkCompleteModal from './assignment-bulk-complete-modal.vue';
import { mapGetters } from "vuex";
import axios from "axios";

const activityTaskStatus = {
  WESTOP_ACTIVITY :0,
  WESTOP_PROJECT: 1,
  WESTOP_PARENT_ACTIVITY: 5,
};


export default {
  name: "activity-assignments",
  components: {
    assignmentTaskTable,
    taskModal,
    assignmentUpdateModal,
    taskUpdateModal,
    assignmentCompletedModal,
    assignmentRegisterTaskModal,
    assignmentDocumentPreview,
    assignmentCommentTaskModal,
    assignmentLineEditModal,
    AssignmentValidateQuantityModal,
    assignmentBulkCompleteModal,
  },
  props: {
    listTasks: {type: Array, required: true },
    canAdd: {type: Boolean, required: true},
    canManage: {type: Boolean, required: true},
    entityId: {type: Number, required: true},
    entityType: {type: String, required: true},
    subactivityId: {type: Number, required: false, default: null},
    activityName: {type: String, required: false, default: ''},
    taskStatus: {type: Number, default: null},
    parentTaskStatus: {type: Number, default: null},
  },
  data() {
    return {
      isLoadingTask: false,
      isTaskEditing: false,
      editedTaskList: [],
      originalListTasks: [],
      /** IDs de tareas con subtareas desplegadas (compartido en toda la jerarquía recursiva). */
      expandedTaskIds: {},
      /** IDs con historial de líneas abierto (persiste tras refresh silencioso). */
      openHistoryTaskIds: {},
      _applyingSilentReload: false,
      isBulkCompleting: false,
      isSavingBulkComplete: false,
      /** Tareas marcadas para completar { [assignmentId]: true }. */
      bulkSelectedTaskIds: {},
    };
  },
  watch: {
    /**
     * Red de seguridad (p.ej. notificación que reemplazó listTasks sin pasar por
     * applySilentTaskReload): si hay nodos marcados como abiertos sin children, rehidratar.
     */
    listTasks: {
      handler(tasks) {
        if (this._applyingSilentReload) {
          return;
        }
        if (!tasks || !tasks.length || !Object.keys(this.expandedTaskIds).length) {
          return;
        }
        if (this.expandedNodesMissingChildren(tasks)) {
          this.refreshExpandedTaskLevel(tasks);
        }
      },
    },
  },
  created() {
    // console.log("assignments in created");
    // console.log(this.assignments);
    // console.log('props', this.$props);

  },
  computed: {
    ...mapGetters({
      locale: "lang/locale",
      user: "auth/user",
      company: 'app/company',
      project: 'app/project',
    }),

    canBulkCompleteTasks() {
      return !this.isTaskEditing && !this.isLockAssignmentTask()
        && this.listTasks.some((row) => this.hasBulkCompletableWork(row))
    },

    bulkCompleteTaskIds() {
      return Object.keys(this.bulkSelectedTaskIds)
        .filter((id) => this.bulkSelectedTaskIds[id])
        .map((id) => Number(id))
    },

    registerProgressLabel() {
      let label = this.$t('register_progress');
      if (this.company.alias == 'welink') {
        label = this.$t('progress_to_inspect');
      }
      return label;
    },
  },
  methods: {
    // tarea (o rama con subtareas) que aún se puede completar en lote
    hasBulkCompletableWork(row) {
      if (row.completed) return false
      if ((row.lines || []).some((line) => line.status == 23)) return false
      if (row.children_count > 0) return true
      const pending = (row.lines || [])
        .filter((line) => line.status == 1)
        .reduce((sum, line) => sum + (parseFloat(line.executed_quantity) || 0), 0)
      return 100 - (parseFloat(row.executed_quantity) || 0) - pending > 0.005
    },
    editTasksButtonClicked() {

      if (!this.isTaskEditing) {
        // copiamos la lista original para poder restaurarla en caso de cancelar
        this.originalListTasks = JSON.parse(JSON.stringify(this.listTasks));
        this.isTaskEditing = true;
      } else {
        this.isTaskEditing = false;
        const restored = JSON.parse(JSON.stringify(this.originalListTasks));
        this.$emit('update:listTasks', restored)
        this.originalListTasks = [];
        this.editedTaskList = [];
      }
    },

    reload() {
      this.$emit('reload');
    },

    cancelBulkComplete() {
      this.isBulkCompleting = false;
      this.bulkSelectedTaskIds = {};
    },

    onBulkSelect(ids, value) {
      const selected = { ...this.bulkSelectedTaskIds };
      ids.forEach((id) => {
        if (value) {
          selected[id] = true;
        } else {
          delete selected[id];
        }
      });
      this.bulkSelectedTaskIds = selected;
    },

    onBulkCompleteTasks() {
      if (this.bulkCompleteTaskIds.length === 0) return;
      this.$refs.assignment_bulk_complete_modal.show(this.bulkCompleteTaskIds.length);
    },

    async submitBulkCompleteTasks({ date, observation }) {
      const ids = [...new Set(this.bulkCompleteTaskIds)];
      if (ids.length === 0) return;
      this.isSavingBulkComplete = true;
      const completed = [];
      const pendingValidation = [];
      const skipped = [];
      try {
        // tandas de 100 (límite del endpoint)
        for (let i = 0; i < ids.length; i += 100) {
          const { data } = await axios.post('/api/v2/assignments/complete-tasks', {
            project_id: this.project.id,
            assignment_ids: ids.slice(i, i + 100),
            date,
            observation,
          });
          if (!data || !data.success) throw new Error();
          completed.push(...(data.completed || []));
          pendingValidation.push(...(data.pending_validation || []));
          skipped.push(...(data.skipped || []));
        }
        if (completed.length > 0) {
          this.$toast.open({ message: this.$t('complete_selected_tasks_success', { count: completed.length }), type: 'is-success', position: 'is-top-right' });
        }
        if (pendingValidation.length > 0) {
          this.$toast.open({ message: this.$t('complete_selected_tasks_pending_validation', { count: pendingValidation.length }), type: 'is-info', position: 'is-top-right' });
        }
        if (skipped.length > 0) {
          this.$toast.open({ message: this.$t('complete_selected_tasks_skipped', { count: skipped.length }), type: 'is-warning', position: 'is-top-right' });
        }
        this.cancelBulkComplete();
        this.reload();
      } catch (e) {
        this.$toast.open({ message: this.$t('update_error'), type: 'is-danger', position: 'is-top-right' });
        if (completed.length > 0 || pendingValidation.length > 0) this.reload();
      } finally {
        this.isSavingBulkComplete = false;
        this.$refs.assignment_bulk_complete_modal.close();
      }
    },

    /**
     * Aplica datos frescos de la raíz sin loading y reconsulta solo niveles desplegados.
     * Importante: emitir al padre, esperar nextTick (prop actualizada) y luego refrescar
     * children sobre ESA lista; si no, se mutaba el árbol viejo y el progreso no se veía.
     * Usado tanto tras registro local como tras notificación de otro usuario.
     */
    async applySilentTaskReload(freshRootTasks) {
      this._applyingSilentReload = true;
      try {
        const merged = this.mergeTaskTreePreservingExpanded(
          this.listTasks,
          freshRootTasks || [],
          this.expandedTaskIds
        );
        this.$emit('update:listTasks', merged);
        await this.$nextTick();
        await this.refreshExpandedTaskLevel(this.listTasks);
      } finally {
        this._applyingSilentReload = false;
      }
    },

    expandedNodesMissingChildren(tasks) {
      const findNode = (nodes, id) => {
        for (const node of nodes || []) {
          if (node.id === id) {
            return node;
          }
          const nested = findNode(node.children, id);
          if (nested) {
            return nested;
          }
        }
        return null;
      };

      return Object.keys(this.expandedTaskIds).some((id) => {
        const node = findNode(tasks, Number(id) || id);
        return !node || !node.children || !node.children.length;
      });
    },

    mergeTaskTreePreservingExpanded(oldTasks, newTasks, expandedTaskIds) {
      const oldById = {};
      const index = (tasks) => {
        (tasks || []).forEach((task) => {
          oldById[task.id] = task;
          if (task.children && task.children.length) {
            index(task.children);
          }
        });
      };
      index(oldTasks);

      return (newTasks || []).map((task) => {
        const previous = oldById[task.id];
        if (previous && expandedTaskIds[task.id] && previous.children && previous.children.length) {
          // Placeholder temporal para no colapsar UI; se sustituye en refreshExpandedTaskLevel.
          this.$set(task, 'children', previous.children);
        }
        return task;
      });
    },

    async refreshExpandedTaskLevel(tasks) {
      if (!tasks || !tasks.length) {
        return;
      }

      const expanded = tasks.filter((task) => this.expandedTaskIds[task.id]);
      await Promise.all(expanded.map(async (task) => {
        try {
          const url = `/api/v2/assignments/activity/${this.entityId}`;
          const { data } = await axios.get(url, {
            params: {
              withLines: 1,
              where_type: 'task',
              parent_assignment_id: task.id,
            },
          });

          if (!(data && data.success)) {
            return;
          }

          const freshChildren = data.assignments || [];
          const previousById = {};
          (task.children || []).forEach((child) => {
            previousById[child.id] = child;
          });

          freshChildren.forEach((child) => {
            const previous = previousById[child.id];
            if (
              previous
              && this.expandedTaskIds[child.id]
              && previous.children
              && previous.children.length
            ) {
              this.$set(child, 'children', previous.children);
            }
          });

          this.$set(task, 'children', freshChildren);
          await this.refreshExpandedTaskLevel(task.children);
        } catch (e) {
          // El toast de error lo maneja la tabla en la carga manual; aquí no interrumpimos el árbol.
        }
      }));
    },

    /**
     * Abre el modal para añadir tarea
     */
    addTaskButtonClicked() {
      this.$refs.task_modal.show();
    },

    isLockAssignmentTask(){
      // primero verificamos si proyecto tiene westop
      if(this.project.has_westop){
        return true
      } 

      // si la actividad es westop activity
      if(this.taskStatus == activityTaskStatus.WESTOP_ACTIVITY){
        return true
      }
      // si la actividad es westop parent activity
      if(this.parentTaskStatus == activityTaskStatus.WESTOP_PARENT_ACTIVITY){
        return true
      }
      return false
    },


    calculeWeight() {
      return parseFloat(
        this.listTasks.reduce((total, item) => {
          const weight = Number(item.weight); // Convertimos el peso a número
          return total + (isNaN(weight) ? 0 : weight); // Si no es un número, suma 0
        }, 0).toFixed(5) // assignments.weight es decimal(8, 5)
      );
    },
    calculePonderation() {
      return parseFloat(
        this.listResources.reduce((total, item) => {
          const weight = Number(item.weight); // Convertimos el peso a número
          return total + (isNaN(weight) ? 0 : weight); // Si no es un número, suma 0
        }, 0).toFixed(5) // assignments.weight es decimal(8, 5)
      );
    },

    saveTasksButtonClicked() {
      // primero comprobamos si la ponderación es correcta
      if (this.calculeWeight() !== 100.00) {
        return;
      }
      // verificamos si hay cambios
      if (this.editedTaskList.length === 0) {
        this.isTaskEditing = false;
        return;
      }
      this.editingLoading = true;
      let url = "/api/v2/assignments/edit/activity/" + this.entityId;
      const params = {
        assignment_resources: this.editedTaskList,
      };
      axios.put(url, params)
        .then((response) => {
          if (response.data.success) {
            this.$toast.open({
              message: this.$t("save_successful"),
              type: "is-success",
              position: "is-top-right"
            });
            this.isTaskEditing = false;
            this.editedTaskList = [];
          } else {
            this.$toast.open({
              message: this.$t("error_saving"),
              type: "is-danger",
              position: "is-top-right"
            });
          }
          this.editingLoading = false;
          this.$emit("reload");
        })
        .catch(() => {
          this.$toast.open({
            message: this.$t("error_saving"),
            type: "is-danger",
            position: "is-top-right"
          });
          this.editingLoading = false;
        });
    },

    distributeTaskWeights() {
      const total = 100
      const count = this.listTasks.length

      if (count === 0) return

      const weightScale = 100000 // assignments.weight es decimal(8, 5)
      const base = Math.floor((total / count) * weightScale) / weightScale
      let sum = 0

      for (let i = 0; i < count; i++) {
        const task = this.listTasks[i]
        if (i < count - 1) {
          task.weight = base
          sum += base
        } else {
          task.weight = Math.round((total - sum) * weightScale) / weightScale
        }

        // Reutiliza la lógica de edición
        this.onEditTask(task, 'weight')
      }
    },

    onEditTask(task, field) {
      // Creamos un objeto con solo los campos necesarios
      if (!['name', 'weight'].includes(field)) {
        return;
      }
      const editedTask = {
        id: task.id,
        [field]: task[field],
      };

      // Buscamos si ya existe en la lista de editados
      let edited = this.editedTaskList.find((item) => item.id === task.id);

      if (!edited) {
        this.editedTaskList.push(editedTask);
      } else {
        edited[field] = task[field];
      }
    },
  },
};
</script>